import React from 'react';

import { Link } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenGo,
  useScreenTitle,
  useScreenState,
  useScreenDispatch,
} from '@gogame/screens';

import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import {
  ApiIntegrationFormCreate,
  ApiIntegrationFormEdit,
} from 'scenes/ApiIntegrations/components/ApiIntegrationForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_OWNER,
  COLUMN_PARTNER_COMPANY,
  COLUMN_AFFILIATES,
  COLUMN_NETWORKS,
  COLUMN_LAST_UPDATED_BY,
  COLUMN_LAST_UPDATED_DATE,
  COLUMN_CREATED_DATE,
  COLUMN_START_DATE,
} from 'scenes/ApiIntegrations/constants';
import {
  useIsUpdating,
  useLoadPartnerApiGrid,
} from 'scenes/ApiIntegrations/data';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/ApiIntegrations/screenReducer';
import PartnerCompanyDetails from 'scenes/PartnerCompanies/components/PartnerCompanyDetails';

import DataGrid from '@gogame/data-grid';

import PartnerApiActions from '../Actions';
import Toolbar from './Toolbar';

import { ScreenLink } from '@gogame/screens';

import { apiIntegrationsDetailsLink } from 'scenes/ApiIntegrations/navigation';

const DetailsLink = ({ value, row }) => {
  return (
    <ScreenLink link={apiIntegrationsDetailsLink} pathParams={{ id: row.id }}>
      {value}
    </ScreenLink>
  );
};

const ApiIntegrationDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const [childComponent, openChildComponent] = useChildComponent();
  const go = useScreenGo();
  const { data, isLoading, isRefetching } = useLoadPartnerApiGrid({
    filters: screenState.filters,
    tableSettings: screenState.tableSettings,
    searchQuery: screenState.searchQuery,
  });

  const isUpdating = useIsUpdating();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(ApiIntegrationFormEdit, { id });
  };

  const handleOpenPartnerCompany = (_e, { id }) => {
    openChildComponent(PartnerCompanyDetails, { id });
  };

  const handleOpenAffiliates = (_e, { id }) => {
    openChildComponent(AffiliateDetails, { id });
  };

  const handleCreate = () => {
    openChildComponent(ApiIntegrationFormCreate, {
      onSubmit: (response) => {
        go(response.id);
      },
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching || isUpdating}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar onCreate={handleCreate} />}
        storageKey={'api-integrations'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Name'}
          minWidth={210}
          fixed={true}
          resizable={false}
          flex={1}
          type={'link'}
          linkComponent={DetailsLink}
        />

        <DataGrid.Column
          field={COLUMN_PARTNER_COMPANY}
          type={'link'}
          headerName={'Partner Company'}
          flex={1}
          minWidth={210}
          onLinkClick={handleOpenPartnerCompany}
        />
        <DataGrid.Column
          field={COLUMN_AFFILIATES}
          type={'list'}
          headerName={'Affiliates'}
          flex={1}
          sortable={false}
          minWidth={210}
          limit={1}
          getItemValue={(v) => {
            return (
              <Link
                onClick={(e) => handleOpenAffiliates(e, v)}
                // style={{ fontSize: '13px' }}
              >
                {v.name}
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_NETWORKS}
          type={'text'}
          headerName={'Networks'}
          flex={1}
          minWidth={210}
        />
        <DataGrid.Column
          field={COLUMN_START_DATE}
          headerName={'Integration Start Date'}
          width={135}
          type={'date'}
          template={'LL'}
          sortable={false}
        />

        <DataGrid.Column
          field={COLUMN_OWNER}
          headerName={'Owner'}
          width={170}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_BY}
          headerName={'Last updated by'}
          width={170}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_DATE}
          headerName={'Last updated'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={135}
          type={'date'}
          template={'LLL'}
          sortable={false}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={PartnerApiActions}
          onEdit={handleEdit}
          limit={2}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default ApiIntegrationDataTable;
